import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import {
    CreateCustomActInputDto,
    CreateCustomContractInputDto,
    CustomActWithType,
    UpdateCustomActInputDto,
    UpdateCustomContractInputDto,
} from '@synapex/shared2/gateway-rest-api'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { apiQueryKey } from '../query-key'
import { OmitWwld } from '../utils'
import {
    createCustomAct,
    createCustomContract,
    updateCustomAct,
    updateCustomContract,
} from './ord.service'

export function useCreateCustomContract(wwld: WorkspaceWithLinkDescriptor) {
    const queryClient = useQueryClient()

    return useMutation<unknown, Error, OmitWwld<CreateCustomContractInputDto>>({
        mutationFn: (input) => createCustomContract({ ...wwld, ...input }),
        onSuccess: (_, input) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.contracts(
                    wwld,
                    input.ad_distributor_contragent_id
                ),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })
        },
    })
}

type UpdateCustomContractVariables = {
    customContractId: number
    ad_distributor_contragent_id: number
} & OmitWwld<UpdateCustomContractInputDto>

export function useUpdateCustomContract(wwld: WorkspaceWithLinkDescriptor) {
    const queryClient = useQueryClient()

    return useMutation<unknown, Error, UpdateCustomContractVariables>({
        mutationFn: ({
            customContractId,
            ad_distributor_contragent_id: _,
            ...input
        }) => updateCustomContract(customContractId, { ...wwld, ...input }),
        onSuccess: (_, variables) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.contracts(
                    wwld,
                    variables.ad_distributor_contragent_id
                ),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })
        },
    })
}

export function useCreateCustomAct(wwld: WorkspaceWithLinkDescriptor) {
    const queryClient = useQueryClient()

    return useMutation<
        CustomActWithType,
        Error,
        OmitWwld<CreateCustomActInputDto>
    >({
        mutationFn: (input) => createCustomAct({ ...wwld, ...input }),
        onSuccess: (_, input) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.acts(
                    wwld,
                    input.ad_distributor_contragent_id
                ),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })
        },
    })
}

type UpdateCustomActVariables = {
    customActId: number
    ad_distributor_contragent_id: number
} & OmitWwld<UpdateCustomActInputDto>

export function useUpdateCustomAct(wwld: WorkspaceWithLinkDescriptor) {
    const queryClient = useQueryClient()

    return useMutation<unknown, Error, UpdateCustomActVariables>({
        mutationFn: ({ customActId, ...input }) =>
            updateCustomAct(customActId, { ...wwld, ...input }),
        onSuccess: (_, variables) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.acts(
                    wwld,
                    variables.ad_distributor_contragent_id
                ),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })
        },
    })
}
